import { useState } from "react";
import { Icons } from "@admin/components/icons/Icons";
import { Modal } from "@admin/components/ui/Modal";
import { AdminTable } from "@admin/components/shared/AdminTable";
import { Pagination } from "@admin/components/ui/Pagination";
import { CreateCatalogModal } from "@admin/components/modals/CreateCatalogModal";
import { useDebounce, useNotifications } from "@admin/hooks";
import {
  useCatalogs,
  useCreateCatalog,
  useUpdateCatalog,
  useToggleCatalogStatus,
  useDeleteCatalog,
} from "@admin/hooks";
import type { Catalog, CreateCatalogData } from "@admin/types/catalog";
import { AxiosError } from "axios";

export function AdminCatalogosPage() {
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingCatalog, setEditingCatalog] = useState<Catalog | undefined>(
    undefined
  );
  const [isStatusModalOpen, setIsStatusModalOpen] = useState(false);
  const [catalogToToggle, setCatalogToToggle] = useState<
    Catalog | undefined
  >(undefined);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [catalogToDelete, setCatalogToDelete] = useState<
    Catalog | undefined
  >(undefined);

  const { showSuccess, showError } = useNotifications();

  // Aplicar debounce al searchQuery (500ms de delay)
  const debouncedSearchQuery = useDebounce(searchQuery, 500);

  const {
    data: catalogsResponse,
    isLoading,
    isError,
    error,
  } = useCatalogs({
    page: currentPage,
    limit: pageSize,
    name: debouncedSearchQuery || undefined,
    includeDeleted: false,
  });

  const catalogs: Catalog[] = catalogsResponse?.data ?? [];
  const paginationMeta = catalogsResponse?.pagination;
  const totalPages = paginationMeta?.totalPages ?? 1;
  const totalItems = paginationMeta?.total ?? catalogs.length;
  const activePage = paginationMeta?.page ?? currentPage;

  // Mutaciones
  const createCatalogMutation = useCreateCatalog();
  const updateCatalogMutation = useUpdateCatalog();
  const toggleCatalogStatusMutation = useToggleCatalogStatus();
  const deleteCatalogMutation = useDeleteCatalog();

  // Columnas de la tabla
  const columns = [
    {
      header: "NOMBRE",
      accessor: "name" as keyof Catalog,
    },
    {
      header: "SLUG",
      accessor: "slug" as keyof Catalog,
    },
    {
      header: "DESTACADO",
      accessor: (row: Catalog) =>
        row.featured ? (
          <span className="px-2 py-1 text-xs font-medium rounded-full bg-yellow-100 text-yellow-800">
            Sí
          </span>
        ) : (
          <span className="px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-600">
            No
          </span>
        ),
    },
    {
      header: "ORDEN",
      accessor: (row: Catalog) => row.order,
    },
    {
      header: "ESTADO",
      accessor: (row: Catalog) => (
        <span
          className={`px-2 py-1 text-xs font-medium rounded-full ${
            row.active
              ? "bg-gray-200 text-gray-800"
              : "bg-red-100 text-red-800"
          }`}
        >
          {row.active ? "Activo" : "Inactivo"}
        </span>
      ),
    },
    {
      header: "CREADO",
      accessor: (row: Catalog) =>
        new Date(row.createdAt).toLocaleDateString("es-AR"),
    },
  ];

  // Acciones para cada fila
  const renderActions = (catalog: Catalog) => (
    <div className="flex space-x-2">
      <button
        onClick={() => handleEdit(catalog)}
        className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
        title="Editar catálogo"
        key="edit"
      >
        <Icons.Edit size={16} />
      </button>
      <button
        onClick={() => handleToggleStatus(catalog)}
        className={`p-2 rounded-lg transition-colors ${
          catalog.active
            ? "text-red-600 hover:bg-red-50"
            : "text-[#282828] hover:bg-gray-100"
        }`}
        title={catalog.active ? "Desactivar catálogo" : "Activar catálogo"}
        key="toggle-status"
      >
        {catalog.active ? (
          <Icons.EyeOff size={16} />
        ) : (
          <Icons.Eye size={16} />
        )}
      </button>
      <button
        onClick={() => handleDelete(catalog)}
        className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
        title="Eliminar catálogo"
        key="delete"
      >
        <Icons.Trash size={16} />
      </button>
    </div>
  );

  const handleEdit = (catalog: Catalog) => {
    setEditingCatalog(catalog);
    setIsModalOpen(true);
  };

  const handleToggleStatus = (catalog: Catalog) => {
    setCatalogToToggle(catalog);
    setIsStatusModalOpen(true);
  };

  const handleDelete = (catalog: Catalog) => {
    setCatalogToDelete(catalog);
    setIsDeleteModalOpen(true);
  };

  const handleConfirmToggleStatus = () => {
    if (catalogToToggle) {
      toggleCatalogStatusMutation.mutate(
        {
          id: catalogToToggle.id,
          currentActive: catalogToToggle.active,
        },
        {
          onSuccess: () => {
            const action = catalogToToggle.active
              ? "desactivado"
              : "activado";
            showSuccess(
              "Estado actualizado",
              `El catálogo "${catalogToToggle.name}" ha sido ${action} exitosamente.`,
              4000
            );
          },
          onError: () => {
            showError(
              "Error al cambiar estado",
              "No se pudo cambiar el estado del catálogo. Por favor, inténtalo de nuevo.",
              6000
            );
          },
        }
      );
      setIsStatusModalOpen(false);
      setCatalogToToggle(undefined);
    }
  };

  const handleCancelToggleStatus = () => {
    setIsStatusModalOpen(false);
    setCatalogToToggle(undefined);
  };

  const handleConfirmDelete = () => {
    if (catalogToDelete) {
      deleteCatalogMutation.mutate(catalogToDelete.id, {
        onSuccess: () => {
          showSuccess(
            "Catálogo eliminado",
            `El catálogo "${catalogToDelete.name}" ha sido eliminado exitosamente.`,
            4000
          );
        },
        onError: () => {
          showError(
            "Error al eliminar",
            "No se pudo eliminar el catálogo. Por favor, inténtalo de nuevo.",
            6000
          );
        },
      });
      setIsDeleteModalOpen(false);
      setCatalogToDelete(undefined);
    }
  };

  const handleCancelDelete = () => {
    setIsDeleteModalOpen(false);
    setCatalogToDelete(undefined);
  };

  const handleAddCatalog = () => {
    setEditingCatalog(undefined);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingCatalog(undefined);
  };

  const handleSubmitCatalog = async (data: CreateCatalogData) => {
    try {
      if (editingCatalog) {
        await updateCatalogMutation.mutateAsync({
          id: editingCatalog.id,
          ...data,
        });
        showSuccess(
          "Catálogo actualizado",
          `El catálogo "${data.name}" se ha actualizado exitosamente.`,
          4000
        );
      } else {
        await createCatalogMutation.mutateAsync(data);
        showSuccess(
          "Catálogo creado",
          `El catálogo "${data.name}" se ha creado exitosamente.`,
          4000
        );
      }

      handleCloseModal();
    } catch (err) {
      const axiosError = err as AxiosError<{
        success?: boolean;
        error?: string;
        message?: string;
      }>;

      if (axiosError.response?.status === 409) {
        showError(
          "Catálogo duplicado",
          axiosError.response.data?.message ||
            "Ya existe un catálogo con ese nombre.",
          6000
        );
      } else if (axiosError.response?.status === 400) {
        showError(
          "Error de validación",
          axiosError.response.data?.message ||
            "Los datos proporcionados no son válidos.",
          6000
        );
      } else if (axiosError.response?.status === 404) {
        showError(
          "Catálogo no encontrado",
          "El catálogo que intentas editar no existe.",
          6000
        );
      } else if (axiosError.response?.status === 500) {
        showError(
          "Error del servidor",
          "Ocurrió un error en el servidor. Por favor, inténtalo más tarde.",
          6000
        );
      } else {
        showError(
          "Error al guardar catálogo",
          "No se pudo guardar el catálogo. Por favor, inténtalo de nuevo.",
          6000
        );
      }
    }
  };

  return (
    <div className="space-y-6">
      {/* Tabla de catálogos */}
      <AdminTable
        title="Gestión de Catálogos"
        subtitle="Administra los catálogos de productos del sistema"
        searchable={true}
        searchValue={searchQuery}
        onSearchChange={(value) => {
          setSearchQuery(value);
          setCurrentPage(1);
        }}
        searchPlaceholder="Buscar catálogos..."
        columns={columns}
        data={catalogs}
        isLoading={isLoading}
        isError={isError}
        error={error?.message || undefined}
        renderActions={renderActions}
        actionsHeader="Acciones"
        maxHeight="500px"
        pagination={
          <Pagination
            currentPage={activePage}
            totalPages={totalPages}
            totalItems={totalItems}
            pageSize={pageSize}
            onPageChange={setCurrentPage}
          />
        }
        onCreateClick={handleAddCatalog}
        createButtonText="Agregar Catálogo"
      />

      {/* Modal de formulario */}
      <CreateCatalogModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onSubmit={handleSubmitCatalog}
        catalog={editingCatalog}
        isLoading={
          editingCatalog
            ? updateCatalogMutation.isPending
            : createCatalogMutation.isPending
        }
      />

      {/* Modal de confirmación de cambio de estado */}
      <Modal
        isOpen={isStatusModalOpen}
        onClose={handleCancelToggleStatus}
        title={
          catalogToToggle?.active
            ? "¿Desactivar catálogo?"
            : "¿Activar catálogo?"
        }
        size="sm"
      >
        <div className="space-y-4">
          <p className="text-gray-600">
            {catalogToToggle?.name} -{" "}
            {catalogToToggle?.active ? "Activo" : "Inactivo"}
          </p>

          <div className="flex justify-end space-x-3 pt-4">
            <button
              onClick={handleCancelToggleStatus}
              className="px-4 py-2 text-[#282828] bg-white border border-[#282828] rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#282828] focus:border-[#282828] transition-colors"
              disabled={toggleCatalogStatusMutation.isPending}
            >
              Cancelar
            </button>
            <button
              onClick={handleConfirmToggleStatus}
              disabled={toggleCatalogStatusMutation.isPending}
              className="px-4 py-2 text-white bg-[#282828] border border-transparent rounded-md hover:bg-[#3a3a3a] focus:outline-none focus:ring-2 focus:ring-[#282828] focus:border-[#282828] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {toggleCatalogStatusMutation.isPending ? (
                <span className="flex items-center">
                  <svg
                    className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Procesando...
                </span>
              ) : (
                "Confirmar"
              )}
            </button>
          </div>
        </div>
      </Modal>

      {/* Modal de confirmación de eliminación */}
      <Modal
        isOpen={isDeleteModalOpen}
        onClose={handleCancelDelete}
        title="¿Eliminar catálogo?"
        size="sm"
      >
        <div className="space-y-4">
          <p className="text-gray-600">
            ¿Estás seguro de que deseas eliminar el catálogo "
            {catalogToDelete?.name}"? Esta acción no se puede deshacer.
          </p>

          <div className="flex justify-end space-x-3 pt-4">
            <button
              onClick={handleCancelDelete}
              className="px-4 py-2 text-[#282828] bg-white border border-[#282828] rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#282828] focus:border-[#282828] transition-colors"
              disabled={deleteCatalogMutation.isPending}
            >
              Cancelar
            </button>
            <button
              onClick={handleConfirmDelete}
              disabled={deleteCatalogMutation.isPending}
              className="px-4 py-2 text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {deleteCatalogMutation.isPending ? (
                <span className="flex items-center">
                  <svg
                    className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Eliminando...
                </span>
              ) : (
                "Eliminar"
              )}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

